// Implements: F-UIX-018, F-USG-005, F-USG-006
/**
 * The three reads of "My usage", cached per person (UIX.4, checklist 14).
 *
 * Every key carries the id of the person the session says is signed in. The routes are
 * self-only, so the URL is the same for everybody: without the id in the key, a second
 * person signing in on the same tab would be shown the first one's cached figures, and an
 * answer still in flight for the first would land on the second's screen. With it, an
 * answer is only ever read back under the person it was asked for, and a stale one is
 * ignored by id.
 */

import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";

import type { ApiError } from "../../../shared/api/ApiError";
import type { MyConsumptionView, MyMockupsView, MyUsageSummaryView } from "../../../types/reports";
import { fetchMyConsumption, fetchMyMockups, fetchMyUsageSummary } from "../api/myUsageApi";

/** Every backoffice read lives under this prefix. */
export const BACKOFFICE_KEY = ["backoffice"] as const;

export function myUsageKey(
  personId: string,
  part: "summary" | "consumption" | "mockups",
): readonly ["backoffice", "me", string, string] {
  return ["backoffice", "me", personId, part];
}

export interface MyUsageReads {
  readonly summary: UseQueryResult<MyUsageSummaryView, ApiError>;
  readonly consumption: UseQueryResult<MyConsumptionView, ApiError>;
  readonly mockups: UseQueryResult<MyMockupsView, ApiError>;
}

/** @param personId The signed-in person, from the session: never typed by anybody. */
export function useMyUsage(personId: string): MyUsageReads {
  const summary = useQuery<MyUsageSummaryView, ApiError>({
    queryKey: myUsageKey(personId, "summary"),
    queryFn: ({ signal }) => fetchMyUsageSummary(signal),
  });
  const consumption = useQuery<MyConsumptionView, ApiError>({
    queryKey: myUsageKey(personId, "consumption"),
    queryFn: ({ signal }) => fetchMyConsumption(signal),
  });
  const mockups = useQuery<MyMockupsView, ApiError>({
    queryKey: myUsageKey(personId, "mockups"),
    queryFn: ({ signal }) => fetchMyMockups(signal),
  });
  return { summary, consumption, mockups };
}

/**
 * The month summary alone, for the sidebar meter (UX-03). Same key as `useMyUsage`, so opening
 * My usage reads nothing twice. `enabled` is `false` while there is no session yet.
 */
export function useMyUsageSummary(personId: string, enabled = true): UseQueryResult<MyUsageSummaryView, ApiError> {
  return useQuery<MyUsageSummaryView, ApiError>({
    queryKey: myUsageKey(personId, "summary"),
    queryFn: ({ signal }) => fetchMyUsageSummary(signal),
    enabled,
  });
}
